import { useState } from 'react';
import { Loader2, RotateCcw, X } from 'lucide-react';
import { useCreatePharmacyPurchaseReturn } from '../../services/httpServices/queries';
import { DatePicker } from '../../components/ui/DatePicker';
import type {
  PharmacyGoodsReceipt,
  PharmacyGoodsReceiptItem,
  PharmacyPaymentMethod,
} from '../../services/httpServices/pharmacyPurchasingService';

interface PharmacyPurchaseReturnDialogProps {
  receipt: PharmacyGoodsReceipt;
  onClose: () => void;
}

const money = (value: number) => `৳${(Number(value) || 0).toLocaleString()}`;

const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const REFUND_METHODS: { value: PharmacyPaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Return-goods-to-supplier dialog for a goods receipt. Quantities are validated
 * against the un-returned balance per line; the credit note value defaults to
 * the value of the returned lines and is applied against the linked payable.
 */
export function PharmacyPurchaseReturnDialog({
  receipt,
  onClose,
}: PharmacyPurchaseReturnDialogProps) {
  const createReturn = useCreatePharmacyPurchaseReturn();

  const items = receipt.items ?? [];

  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [returnedDate, setReturnedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [refundMethod, setRefundMethod] = useState<PharmacyPaymentMethod>('CASH');
  const [refundReference, setRefundReference] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const remainingFor = (item: PharmacyGoodsReceiptItem) =>
    round(Number(item.quantityReceived) - Number(item.returnedQuantity ?? 0));

  const requestedLines = items
    .map((item) => ({ item, quantity: round(Number(quantities[item.id] ?? 0)) }))
    .filter((line) => line.quantity > 0);

  const creditValue = round(
    requestedLines.reduce((sum, line) => sum + line.quantity * Number(line.item.unitCost), 0),
  );

  const hasSelection = requestedLines.length > 0;
  const invoiceNumber = receipt.invoice?.invoiceNumber;

  const setQuantity = (id: string, value: string) =>
    setQuantities((prev) => ({ ...prev, [id]: value }));

  const handleSubmit = async () => {
    if (!hasSelection || createReturn.isPending) return;
    try {
      await createReturn.mutateAsync({
        grnId: receipt.id,
        data: {
          items: requestedLines.map((line) => ({
            grnItemId: line.item.id,
            quantity: line.quantity,
          })),
          returnedDate: returnedDate || undefined,
          refundMethod,
          refundReference: refundReference.trim() || undefined,
          reason: reason.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      });
      onClose();
    } catch {
      // Error toast is shown by the global MutationCache; keep the dialog open.
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <RotateCcw size={18} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-text">Return to Supplier</h2>
              <p className="text-xs text-text-muted font-mono">{receipt.grnNumber}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background/50 border-b border-border">
                  <th className="px-3 py-2 text-left text-xs text-text-muted uppercase">Item</th>
                  <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Received</th>
                  <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Returnable</th>
                  <th className="px-3 py-2 text-right text-xs text-text-muted uppercase w-24">Return</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((item) => {
                  const remaining = remainingFor(item);
                  return (
                    <tr key={item.id}>
                      <td className="px-3 py-2 text-text">
                        {item.drug?.name || item.drugId}
                        {item.batchNumber && (
                          <span className="ml-1 text-xs text-text-muted">({item.batchNumber})</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-text-muted">
                        {Number(item.quantityReceived)}
                      </td>
                      <td className="px-3 py-2 text-right text-text-muted">
                        {remaining > 0 ? remaining : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          max={remaining}
                          step="any"
                          disabled={remaining <= 0}
                          value={quantities[item.id] ?? ''}
                          placeholder="0"
                          onChange={(e) => setQuantity(item.id, e.target.value)}
                          className="w-20 px-2 py-1 border border-border rounded-lg bg-background text-text text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-40"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-border bg-background/50 p-3 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Credit note value</span>
              <span className="text-text font-medium">{money(creditValue)}</span>
            </div>
            <p className="text-xs text-text-muted">
              {invoiceNumber
                ? `Applied against invoice ${invoiceNumber} to reduce the payable.`
                : 'This receipt has no supplier bill yet; the value will be excluded when the bill is generated.'}
            </p>

            <div>
              <label className="block text-xs text-text-muted uppercase mb-1">Return date</label>
              <DatePicker value={returnedDate} onChange={setReturnedDate} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Settlement</label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value as PharmacyPaymentMethod)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {REFUND_METHODS.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-muted uppercase mb-1">Reference</label>
                <input
                  type="text"
                  value={refundReference}
                  onChange={(e) => setRefundReference(e.target.value)}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-text-muted uppercase mb-1">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Expired / damaged stock"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs text-text-muted uppercase mb-1">Note</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-4 py-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            disabled={createReturn.isPending}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!hasSelection || createReturn.isPending}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {createReturn.isPending && <Loader2 size={16} className="animate-spin" />}
            Record Return
          </button>
        </div>
      </div>
    </div>
  );
}
